import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Ban } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface VoidInvoiceDialogProps {
    open: boolean;
    onClose: () => void;
    invoiceId: string;
    /** Shown in the dialog body for context (e.g. "Invoice #INV-1042"). */
    invoiceLabel?: string | null;
    /** Called after a successful void so the caller can refresh/navigate. */
    onVoided?: () => void;
}

/**
 * Confirmation dialog for voiding (cancelling) an invoice — the clean exit for
 * an invoice that should never have been billed (e.g. one that reads as "sent"
 * but wasn't). A reason is required and kept in the invoice's history. Voiding
 * an invoice is also what unblocks cancelling its job: POST /api/jobs/:id/cancel
 * refuses while a sent/open/paid invoice hangs off the job. The server refuses
 * (409 CANNOT_VOID_WITH_PAYMENTS) if the invoice has recorded payments — reverse
 * those with Reopen or Refund first.
 */
export function VoidInvoiceDialog({ open, onClose, invoiceId, invoiceLabel, onVoided }: VoidInvoiceDialogProps) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");

    const voidInvoice = useMutation({
        mutationFn: () => api.post(`/api/invoices/${invoiceId}/void`, { reason: reason.trim() }),
        onSuccess: () => {
            toast.success("Invoice voided — the job can now be cancelled");
            qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
            qc.invalidateQueries({ queryKey: ["commercial"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["close-out-queue"] });
            setReason("");
            onVoided?.();
            onClose();
        },
        onError: (err: any) => {
            const msg =
                err?.body?.message ??
                (err?.body?.error === "CANNOT_VOID_WITH_PAYMENTS"
                    ? "This invoice has recorded payments. Reopen or refund it first."
                    : null) ??
                err?.body?.error ??
                err?.message ??
                "unknown error";
            toast.error(`Void failed: ${msg}`);
        },
    });

    const canSubmit = reason.trim().length >= 3 && !voidInvoice.isPending;

    return (
        <Dialog open={open} onClose={onClose} title="Void Invoice">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Void {invoiceLabel ? <span className="font-mono font-medium">{invoiceLabel}</span> : "this invoice"}
                    ? It keeps its history but is no longer billable, drops off the customer&apos;s balance, and stops
                    blocking its job from being cancelled. This can&apos;t be undone.
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="void-invoice-reason">Reason *</Label>
                    <Textarea
                        id="void-invoice-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this invoice being voided? (e.g. sent by mistake, never actually emailed)"
                        rows={3}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={voidInvoice.isPending}>
                        Keep Invoice
                    </Button>
                    <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!canSubmit}
                        onClick={() => voidInvoice.mutate()}
                    >
                        {voidInvoice.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Ban className="h-4 w-4 mr-1" />
                        )}
                        Void Invoice
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
